import { useMemo } from "react";
import { useBuildings } from "./useBuildings";
import type { Building } from "./building";

export interface DashboardStats {
  totalBuildings: number;
  alertBuildings: number;
  warnBuildings: number;
  okBuildings: number;
  totalOptimizationPotentialEurMonth: number;
  buildingsWithActiveOptimizations: number;
  totalActiveOptimizations: number;
  cityBreakdown: Record<string, number>;
}

type DashboardStatsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; stats: DashboardStats; buildings: Building[] };

function deriveStats(buildings: Building[]): DashboardStats {
  const cityBreakdown: Record<string, number> = {};
  for (const b of buildings) {
    cityBreakdown[b.city] = (cityBreakdown[b.city] ?? 0) + 1;
  }
  return {
    totalBuildings: buildings.length,
    alertBuildings: buildings.filter((b) => b.status === "alert").length,
    warnBuildings: buildings.filter((b) => b.status === "warn").length,
    okBuildings: buildings.filter((b) => b.status === "ok").length,
    totalOptimizationPotentialEurMonth: buildings.reduce(
      (sum, b) => sum + (b.optimization_potential_eur_month ?? 0),
      0,
    ),
    buildingsWithActiveOptimizations: buildings.filter(
      (b) => b.active_optimizations > 0,
    ).length,
    totalActiveOptimizations: buildings.reduce(
      (sum, b) => sum + b.active_optimizations,
      0,
    ),
    cityBreakdown,
  };
}

/** Derives inventory-level stats from the buildings list. Shares the same
 *  fetch as `useBuildings` — the hook instances are independent, so the
 *  caller can use either. */
export function useDashboardStats(): DashboardStatsState {
  const buildingsState = useBuildings();
  return useMemo<DashboardStatsState>(() => {
    if (buildingsState.status === "loading") return { status: "loading" };
    if (buildingsState.status === "error")
      return { status: "error", message: buildingsState.message };
    return {
      status: "ok",
      stats: deriveStats(buildingsState.buildings),
      buildings: buildingsState.buildings,
    };
  }, [buildingsState]);
}
